import { useRef, useState, ChangeEvent } from "react";
import ResponsiveImage from "../../../components/ui/images/ResponsiveImage";

import type { UseFormRegister, FieldError, FieldValues, Path } from "react-hook-form";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

type ImageInputProps<T extends FieldValues> = {
  className?: string;
  register: UseFormRegister<T>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name: string
  imageUrl?: string
  error?: FieldError;
}

const ImageInput = <T extends FieldValues>({
  className,
  onChange,
  register,
  name,
  imageUrl,
  error
}: ImageInputProps<T>) => {
  const [imagePath, setImagePath] = useState<string | undefined>(imageUrl)
  const { onChange: rhfOnChange, ...restRegister } = register(name as Path<T>, {
    validate: {
      lessThan2MB: (files: FileList) => 
        !files || files.length === 0 || files[0].size < MAX_FILE_SIZE || "Max size is 2MB",
      acceptedFormats: (files: FileList) =>
        !files || files.length === 0 || ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'].includes(files[0]?.type) || "Only JPEG or PNG or WebP"
    }
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (imagePath?.startsWith('blob:')) URL.revokeObjectURL(imagePath);

      const objectUrl = URL.createObjectURL(file);
      setImagePath(objectUrl)
      if (onChange) {
        onChange(event);
      }
    }

    rhfOnChange(event);
  };

const handleRemoveFile = () => {
    if (imagePath?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePath);
    }
    setImagePath(undefined);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    rhfOnChange({
      target: {
        name: name,
        value: null,
      }
    } as unknown as ChangeEvent<HTMLInputElement>)
  };

  return (
    <div>
      { imagePath && 
        <div className="w-full max-w-[400px] overflow-hidden relative rounded-lg">
          <button
            type="button"
            onClick={handleRemoveFile}
            className="absolute right-3 top-3
              z-10 flex h-9.5 w-9.5 items-center justify-center rounded-full 
              bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 
              hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 
              dark:hover:text-white sm:right-3 sm:top-3 sm:h-11 sm:w-11
              border border-gray-600 dark:border-gray-400"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.04289 16.5413C5.65237 16.9318 5.65237 17.565 6.04289 17.9555C6.43342 18.346 7.06658 18.346 7.45711 17.9555L11.9987 13.4139L16.5408 17.956C16.9313 18.3466 17.5645 18.3466 17.955 17.956C18.3455 17.5655 18.3455 16.9323 17.955 16.5418L13.4129 11.9997L17.955 7.4576C18.3455 7.06707 18.3455 6.43391 17.955 6.04338C17.5645 5.65286 16.9313 5.65286 16.5408 6.04338L11.9987 10.5855L7.45711 6.0439C7.06658 5.65338 6.43342 5.65338 6.04289 6.0439C5.65237 6.43442 5.65237 7.06759 6.04289 7.45811L10.5845 11.9997L6.04289 16.5413Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <ResponsiveImage imageUrl={imagePath} />
        </div>
      }
      <div className="relative">
        <input
          type="file"
          {...restRegister}
          accept="image/*"
          className={`
            focus:border-ring-brand-300 h-11 w-full overflow-hidden rounded-lg border bg-transparent text-sm text-gray-500 shadow-theme-xs transition-colors 
            file:mr-5 file:border-collapse file:cursor-pointer file:rounded-l-lg file:border-0 file:border-r file:border-solid file:bg-gray-50 file:py-3 file:pl-3.5 file:pr-3 file:text-sm file:text-gray-700 
            hover:file:bg-gray-100 focus:outline-hidden 
            dark:bg-gray-900 dark:text-white/90 dark:file:bg-white/[0.03] dark:file:text-gray-400
            ${error 
              ? "border-red-400 dark:border-red-400/50 file:border-red-400 file:bg-red-40" 
              : "border-gray-300 dark:border-gray-700 file:border-gray-200 dark:file:border-gray-800"
            } 
            ${className}
          `}
          ref={(e) => {
            restRegister.ref(e);
            fileInputRef.current = e;
          }}
          onChange={handleFileChange}
        />

        {error && (
          <p className="mt-1.5 text-xs text-red-500">
            {error.message}
          </p>
        )}
      </div>
      
    </div>
  );
};

export default ImageInput;
